/**
 * The offline engine (PROJECT_SPEC.md §8.2): queue a capture while the server cannot be reached, replay it in
 * order when it can, and show — never hide — what is still only pending.
 *
 * The server is the authority. This module decides only what the DEVICE may accept (an allow-list, the
 * duration limit, the PIN lock); the server re-decides all of it on replay, and anything it refuses comes back
 * as a task the operator must clear (OFF-014). "Pending" is not "recorded", and nothing here says otherwise.
 */
import {
  amend,
  applyDelta,
  applySyncResults,
  type CommandQueue,
  captureGate,
  emptyWorkingSet,
  enqueue,
  knownVersions,
  type LocalAuditEntry,
  localAuditOf,
  manifestOf,
  markSent,
  mbrContentHash,
  offlineVerdict,
  prevalidate,
  type QueuedCommand,
  replayOrder,
  requeueMissing,
  type SyncResult,
  type WorkingSet,
  type WorkingSetDelta,
} from "@corpex-ebr/domain";
import type { CommandEnvelope, DeviceEvidence } from "@corpex-ebr/domain/events/envelope";
import type { MonotonicClock } from "./clock.js";
import type { StoredAnchor, UnlockResult, Vault, VaultStatus } from "./vault.js";

/** What the engine needs from the server; api.ts supplies it, tests supply a fake. */
export interface OfflineApi {
  anchor(b: { deviceId: string; monotonicNs: string; deviceWallClock: string }): Promise<{
    serverTime: string;
    monotonicNs: string;
    maxOfflineHours: number;
  }>;
  openSession(b: { deviceId: string; expectedIds: string[] }): Promise<{ sessionId: string }>;
  sync(b: {
    deviceId: string;
    commands: WireCommand[];
    localAudit: LocalAuditEntry[];
  }): Promise<SyncResult[]>;
  closeSession(id: string): Promise<{ status: "COMPLETE" | "PARTIAL"; missing: string[] }>;
  blockingTasks(batchId: string): Promise<ServerTask[]>;
  resolveRejection(id: string, resolution: string): Promise<unknown>;
  reportLost(deviceId: string, circumstances: string): Promise<unknown>;
  /** URS-8.6-001: `known` is "batchId:streamVersion,..." for what the device already holds. */
  workingSet(known: string): Promise<WorkingSetDelta>;
}

export interface WireCommand {
  clientCommandId: string;
  batchId: string;
  commandType: string;
  payload: Record<string, unknown>;
  actorUserId: string;
  authMethod: "DEVICE_PIN" | "WEBAUTHN";
  unlockedMonotonicNs: string;
  monotonicNs: string;
  deviceTime?: string;
}

export interface ServerTask {
  id: string;
  client_command_id: string;
  rejection_code: string;
  rejection_message: string;
}

/** A refusal the operator can read: the code is for support, the message is for the person at the line. */
export class OfflineRefusal extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "OfflineRefusal";
  }
}

export type SyncPhase = "IDLE" | "SYNCING" | "NEEDS_SIGN_IN" | "WRONG_USER" | "DEVICE_LOST" | "FAILED";

export interface EngineState {
  vault: VaultStatus;
  /** This browser's registered device id, once enrolled. */
  deviceId: string | null;
  queue: CommandQueue;
  anchor: StoredAnchor | null;
  /** The read replica (URS-8.6-001); null until the first online refresh. */
  workingSet: WorkingSet | null;
  phase: SyncPhase;
  /** Last problem to show the operator, if any. */
  notice: string | null;
  /** Milliseconds of offline capture left, null when there is no anchor. */
  remainingMs: number | null;
}

export interface EngineDeps {
  vault: Vault;
  clock: MonotonicClock;
  api: OfflineApi;
  /** The signed-in person's id, or null when there is no valid session. */
  currentUserId: () => string | null;
  now?: () => Date;
  newId?: () => string;
}

const sinceUnsynced = (q: CommandQueue) => q.filter((c) => c.state === "QUEUED" || c.state === "SENT");

export class OfflineEngine {
  private state: EngineState = {
    vault: "NOT_ENROLLED",
    deviceId: null,
    queue: [],
    anchor: null,
    workingSet: null,
    phase: "IDLE",
    notice: null,
    remainingMs: null,
  };
  private readonly listeners = new Set<() => void>();
  private unlockedAtNs = "0";

  constructor(private readonly d: EngineDeps) {}

  getState = (): EngineState => this.state;
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  private set(patch: Partial<EngineState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }
  private newId = () => (this.d.newId ?? (() => crypto.randomUUID()))();
  private now = () => (this.d.now ?? (() => new Date()))();

  // ------------------------------------------------------------------ the device lock (OFF-002, OFF-004)

  async init(): Promise<void> {
    this.set({
      vault: await this.d.vault.status(),
      deviceId: (await this.d.vault.identity())?.deviceId ?? null,
    });
  }

  async enroll(pin: string, deviceId: string): Promise<void> {
    const userId = this.d.currentUserId();
    if (!userId)
      throw new OfflineRefusal("NEEDS_SIGN_IN", "Sign in before setting up this device for offline work.");
    await this.d.vault.enroll(pin, userId, deviceId);
    this.unlockedAtNs = this.d.clock.nowNs();
    await this.refresh();
  }

  async unlock(pin: string): Promise<UnlockResult> {
    const r = await this.d.vault.unlock(pin);
    if (r.ok) {
      this.unlockedAtNs = this.d.clock.nowNs();
      await this.refresh();
    } else this.set({ vault: await this.d.vault.status() });
    return r;
  }

  lock(): void {
    this.d.vault.lock();
    this.set({ vault: "LOCKED", queue: [], anchor: null, workingSet: null, remainingMs: null });
  }

  /** The enrolled person is signed in to the IdP: a locked-out store may be tried again. */
  async clearLockout(): Promise<boolean> {
    const uid = this.d.currentUserId();
    const ok = uid ? await this.d.vault.clearLockout(uid) : false;
    this.set({ vault: await this.d.vault.status() });
    return ok;
  }

  private async refresh(): Promise<void> {
    const vault = await this.d.vault.status();
    if (vault !== "UNLOCKED") return this.set({ vault });
    const [queue, anchor] = [await this.d.vault.loadQueue(), await this.d.vault.loadAnchor()];
    this.set({
      vault,
      queue,
      anchor,
      workingSet: await this.d.vault.loadWorkingSet(),
      deviceId: (await this.d.vault.identity())?.deviceId ?? null,
      remainingMs: this.remaining(anchor),
    });
  }

  private remaining(anchor: StoredAnchor | null): number | null {
    if (!anchor) return null;
    const g = captureGate(anchor, this.d.clock.nowNs(), anchor.maxOfflineMs);
    return g.open ? g.remainingMs : 0;
  }

  /** Re-reads the duration limit; called on a timer so the banner counts down. */
  tick(): void {
    this.set({ remainingMs: this.remaining(this.state.anchor) });
    void this.checkpoint();
  }

  /** Saves the monotonic reading so a reload, crash or killed tab does not lose elapsed offline time. */
  checkpoint(): Promise<void> {
    return this.d.clock.checkpoint();
  }

  // ------------------------------------------------------------------ capture (OFF-009, OFF-010, OFF-011)

  /**
   * Queues a capture. Refuses what the device may not accept: anything not on the allow-list (signatures and
   * approvals above all — OFF-010), anything after the duration limit (OFF-011), anything while locked.
   */
  async capture(
    commandType: string,
    batchId: string,
    payload: Record<string, unknown>,
    clientCommandId = this.newId(),
  ): Promise<QueuedCommand> {
    const verdict = offlineVerdict(commandType, payload);
    if (!verdict.ok) throw new OfflineRefusal(verdict.code, verdict.message);
    const { anchor } = this.state;
    if (this.state.vault !== "UNLOCKED")
      throw new OfflineRefusal(
        "DEVICE_LOCKED",
        this.state.vault === "NOT_ENROLLED"
          ? "The server cannot be reached and this device is not set up for offline work. Open Offline queue and set a PIN first (this needs a connection)."
          : "The server cannot be reached. Unlock this device with your PIN (Offline queue) to record offline.",
      );
    const gate = captureGate(anchor, this.d.clock.nowNs(), anchor?.maxOfflineMs ?? 0);
    if (!gate.open) throw new OfflineRefusal(gate.reason, gate.message);

    this.prevalidateAgainstReplica(commandType, batchId, payload);
    const ident = await this.d.vault.identity();
    const evidence: DeviceEvidence = {
      deviceId: ident?.deviceId ?? "unknown",
      deviceTime: this.now().toISOString(),
      deviceClockSource: "UNKNOWN",
      deviceMonotonicNs: this.d.clock.nowNs(),
      capturedOffline: true,
      lastServerTimeSeen: anchor?.lastServerTimeSeen,
      authMethod: "DEVICE_PIN",
      unlockedMonotonicNs: this.unlockedAtNs,
    };
    const envelope: CommandEnvelope = { clientCommandId, batchId, commandType, payload, device: evidence };
    const queue = enqueue(this.state.queue, envelope, evidence);
    await this.persist(queue);
    return queue.find((c) => c.clientCommandId === clientCommandId) as QueuedCommand;
  }

  /**
   * OFF-009 / §8.2 step 2: the same rule code the server runs, over the replica. A batch the replica does not
   * hold cannot be checked here and is queued for the server to judge; the server decides either way.
   */
  private prevalidateAgainstReplica(
    commandType: string,
    batchId: string,
    payload: Record<string, unknown>,
  ): void {
    const ws = this.state.workingSet;
    const batch = ws?.batches[batchId];
    if (!ws || !batch) return;
    const v = prevalidate(batch, commandType, payload, { now: this.now(), siteTz: ws.siteTz });
    if (!v.ok) throw new OfflineRefusal(v.code, v.message);
  }

  /** OFF-005: an edit before sync is an amendment with a reason, never an overwrite. */
  async amendQueued(
    clientCommandId: string,
    payload: Record<string, unknown>,
    reason: string,
  ): Promise<void> {
    const original = this.state.queue.find((c) => c.clientCommandId === clientCommandId);
    const ident = await this.d.vault.identity();
    if (!original || !ident) throw new OfflineRefusal("NOT_FOUND", "That queued entry no longer exists.");
    const replacement: CommandEnvelope = { ...original.envelope, clientCommandId: this.newId(), payload };
    const out = amend(this.state.queue, clientCommandId, replacement, {
      auditId: this.newId(),
      reason,
      actorUserId: ident.userId,
      deviceTime: this.now().toISOString(),
      monotonicNs: this.d.clock.nowNs(),
    });
    if (!out.ok) throw new OfflineRefusal(out.code, out.message);
    await this.persist(out.queue);
  }

  private async persist(queue: CommandQueue): Promise<void> {
    await this.d.vault.saveQueue(queue);
    await this.d.clock.checkpoint();
    this.set({ queue });
  }

  // ------------------------------------------------------------------ sync (OFF-006, OFF-007, OFF-014)

  /**
   * Replays the queue in order. Order of operations matters: the queue is sent BEFORE a new time anchor is
   * taken, because the server measures each capture against the anchor it was captured under.
   */
  async sync(): Promise<void> {
    if (this.state.vault !== "UNLOCKED" || this.state.phase === "SYNCING") return;
    const identity = await this.d.vault.identity();
    const user = this.d.currentUserId();
    if (!identity) return;
    if (!user)
      return this.set({ phase: "NEEDS_SIGN_IN", notice: "Sign in again to send what was recorded offline." });
    if (user !== identity.userId)
      return this.set({
        phase: "WRONG_USER",
        notice:
          "Another person is signed in. What is queued here was recorded by someone else and can only be sent by them.",
      });
    this.set({ phase: "SYNCING", notice: null });
    try {
      await this.syncQueue(identity);
      await this.refreshAnchor(identity.deviceId);
      await this.refreshWorkingSet();
      this.set({ phase: "IDLE" });
    } catch (e) {
      await this.onSyncFailure(e);
    }
  }

  private async syncQueue({ deviceId, userId }: { deviceId: string; userId: string }): Promise<void> {
    let queue = this.state.queue;
    const manifest = manifestOf(queue);
    if (manifest.length === 0 && localAuditOf(queue).length === 0) return;
    const { sessionId } = manifest.length
      ? await this.d.api.openSession({ deviceId, expectedIds: manifest })
      : { sessionId: null };

    const wire = this.markAllSent(queue, manifest, userId);
    queue = wire.queue;
    await this.persist(queue);
    let results: SyncResult[];
    try {
      results = await this.d.api.sync({ deviceId, commands: wire.commands, localAudit: localAuditOf(queue) });
    } catch (e) {
      // no verdict: nothing is assumed. SENT goes back to QUEUED and the same ids are sent again (OFF-007).
      await this.persist(applySyncResults(queue, []).queue);
      throw e;
    }
    queue = applySyncResults(queue, results).queue;
    if (sessionId) {
      const closed = await this.d.api.closeSession(sessionId);
      if (closed.status === "PARTIAL") queue = requeueMissing(queue, closed.missing);
    }
    // what the server has recorded or kept is no longer this device's to carry; a refusal stays until cleared
    await this.persist(queue.filter((c) => c.state !== "ACCEPTED" && c.state !== "AMENDED"));
  }

  private markAllSent(
    queue: CommandQueue,
    ids: string[],
    userId: string,
  ): { queue: CommandQueue; commands: WireCommand[] } {
    const commands: WireCommand[] = [];
    let q = queue;
    for (const id of ids) {
      const c = q.find((x) => x.clientCommandId === id);
      const m = markSent(q, id);
      if (!c || !m.ok) continue;
      q = m.queue;
      commands.push(toWire(c, userId));
    }
    return { queue: q, commands };
  }

  /** Only when nothing is waiting to be sent: a new anchor would re-base captures made under the old one. */
  private async refreshAnchor(deviceId: string): Promise<void> {
    if (sinceUnsynced(this.state.queue).length > 0) return;
    const monotonicNs = this.d.clock.nowNs();
    const a = await this.d.api.anchor({ deviceId, monotonicNs, deviceWallClock: this.now().toISOString() });
    const anchor: StoredAnchor = {
      lastServerTimeSeen: a.serverTime,
      monotonicAtSync: a.monotonicNs,
      maxOfflineMs: a.maxOfflineHours * 3_600_000,
    };
    await this.d.vault.saveAnchor(anchor);
    this.set({ anchor, remainingMs: this.remaining(anchor) });
  }

  /**
   * URS-8.6-001: asks the server for what changed and replaces those parts of the replica. A batch whose MBR
   * content does not hash to what the server stated is not trusted and is left out, with a notice.
   */
  async refreshWorkingSet(): Promise<void> {
    if (this.state.vault !== "UNLOCKED") return;
    const held = this.state.workingSet ?? emptyWorkingSet;
    try {
      const known = Object.entries(knownVersions(held))
        .map(([id, v]) => `${id}:${v}`)
        .join(",");
      const delta = await this.d.api.workingSet(known);
      const good: WorkingSetDelta["batches"] = [];
      for (const b of delta.batches) if ((await mbrContentHash(b.content)) === b.contentHash) good.push(b);
      const next = applyDelta(held, { ...delta, batches: good });
      await this.d.vault.saveWorkingSet(next);
      this.set({
        workingSet: next,
        ...(good.length < delta.batches.length
          ? { notice: "A batch record's recipe did not match its checksum and was not kept for offline use." }
          : {}),
      });
    } catch {
      // the replica serves reading and checking; the sync itself succeeded, and the earlier copy stays
      this.set({
        notice: "The offline copy of your batches could not be refreshed. The earlier copy is kept.",
      });
    }
  }

  private async onSyncFailure(e: unknown): Promise<void> {
    const code = (e as { failure?: { code?: string } }).failure?.code;
    if (code === "DEVICE_REPORTED_LOST") {
      const n = await this.d.vault.wipe("This device was reported lost.", this.now().toISOString());
      return this.set({
        phase: "DEVICE_LOST",
        vault: "NOT_ENROLLED",
        deviceId: null,
        queue: [],
        anchor: null,
        workingSet: null,
        remainingMs: null,
        notice: `This device was reported lost, so its offline data was removed (${n.commandsDiscarded} unsent entries).`,
      });
    }
    this.set({
      phase: "FAILED",
      notice: e instanceof Error ? e.message : "The queue could not be sent. It is still on this device.",
    });
  }

  // ------------------------------------------------------------------ refusals and lost devices

  /** OFF-014: a refused command stays on the device until the operator has dealt with it on the server. */
  async resolveRejected(clientCommandId: string, resolution: string): Promise<void> {
    const c = this.state.queue.find((x) => x.clientCommandId === clientCommandId);
    if (!c || c.state !== "REJECTED")
      throw new OfflineRefusal("NOT_FOUND", "That entry is not a refused one.");
    const tasks = await this.d.api.blockingTasks(c.batchId);
    const task = tasks.find((t) => t.client_command_id === clientCommandId);
    if (!task) throw new OfflineRefusal("NOT_FOUND", "The server has no open task for that entry.");
    await this.d.api.resolveRejection(task.id, resolution);
    await this.persist(this.state.queue.filter((x) => x.clientCommandId !== clientCommandId));
  }

  async reportLost(deviceId: string, circumstances: string): Promise<void> {
    await this.d.api.reportLost(deviceId, circumstances);
  }

  /** Ordered view of what is waiting, for the screen. */
  pendingFor(batchId: string): QueuedCommand[] {
    return replayOrder(this.state.queue, batchId).filter((c) => c.state === "QUEUED" || c.state === "SENT");
  }
}

function toWire(c: QueuedCommand, actorUserId: string): WireCommand {
  const d = c.device;
  return {
    clientCommandId: c.clientCommandId,
    batchId: c.batchId,
    commandType: c.envelope.commandType,
    payload: c.envelope.payload,
    actorUserId,
    authMethod: d.authMethod ?? "DEVICE_PIN",
    unlockedMonotonicNs: d.unlockedMonotonicNs ?? "0",
    monotonicNs: d.deviceMonotonicNs ?? "0",
    ...(d.deviceTime ? { deviceTime: d.deviceTime } : {}),
  };
}
